import React from 'react';
import { Link } from 'react-router-dom';
import { FaHome, FaChevronRight, FaRocket, FaLaptop, FaUsersCog, FaArrowRight, FaPhone, FaCogs, FaClipboardList, FaServer } from 'react-icons/fa';
import Navigation from '../components/Navigation';
import TopBar from '../components/TopBar';
import Footer from '../components/Footer';

const ITDeploymentPage = () => {
    const services = [
        {
            icon: <FaLaptop size={24} />,
            title: 'Workstation Deployment',
            description: 'Complete setup of desktops, laptops, and peripherals with software configuration and user migration.',
            gradient: 'from-cyan-500 to-blue-500'
        },
        {
            icon: <FaServer size={24} />,
            title: 'Server Deployment',
            description: 'Enterprise server installation, configuration, and integration with existing infrastructure.',
            gradient: 'from-blue-600 to-indigo-600'
        },
        {
            icon: <FaCogs size={24} />,
            title: 'Software Installation',
            description: 'Bulk software deployment and configuration using enterprise management tools.',
            gradient: 'from-purple-600 to-violet-600'
        },
        {
            icon: <FaUsersCog size={24} />,
            title: 'User Migration',
            description: 'Seamless data and profile migration ensuring minimal disruption to end users.',
            gradient: 'from-teal-500 to-green-500'
        },
        {
            icon: <FaClipboardList size={24} />,
            title: 'Asset Management',
            description: 'Complete asset tracking, tagging, and documentation for your IT inventory.',
            gradient: 'from-orange-500 to-amber-500'
        },
        {
            icon: <FaRocket size={24} />,
            title: 'Fast Turnaround',
            description: 'Rapid deployment services to meet tight deadlines and business requirements.',
            gradient: 'from-rose-500 to-pink-500'
        }
    ];

    const process = [
        { step: '1', title: 'Planning', description: 'Requirements gathering and deployment strategy' },
        { step: '2', title: 'Preparation', description: 'Hardware staging and software imaging' },
        { step: '3', title: 'Deployment', description: 'Installation and configuration at your site' },
        { step: '4', title: 'Testing', description: 'Quality assurance and user acceptance' },
        { step: '5', title: 'Handover', description: 'Documentation and training' }
    ];

    return (
        <div className="font-sans">
            <TopBar />
            <Navigation />

            {/* Hero Section */}
            <section className="relative bg-gradient-to-br from-gray-900 via-cyan-900 to-blue-900 py-24 overflow-hidden">
                <div className="absolute inset-0 opacity-10">
                    <div className="absolute inset-0" style={{
                        backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
                    }}></div>
                </div>

                <div className="absolute top-0 right-0 w-96 h-96 bg-cyan-500 rounded-full filter blur-3xl opacity-20 translate-x-1/2 -translate-y-1/2"></div>
                <div className="absolute bottom-0 left-0 w-96 h-96 bg-blue-500 rounded-full filter blur-3xl opacity-20 -translate-x-1/2 translate-y-1/2"></div>

                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                    <nav className="flex items-center gap-2 text-sm text-gray-300 mb-8">
                        <Link to="/" className="flex items-center gap-1 hover:text-white transition-colors">
                            <FaHome size={14} />
                            <span>Home</span>
                        </Link>
                        <FaChevronRight size={10} className="text-gray-500" />
                        <span className="text-cyan-400">IT Deployment</span>
                    </nav>

                    <div className="grid lg:grid-cols-2 gap-12 items-center">
                        <div>
                            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
                                Professional <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-400">IT Deployment</span> Services
                            </h1>
                            <p className="text-xl text-gray-300 mb-8 leading-relaxed">
                                From single workstations to enterprise-wide rollouts, we deploy IT solutions quickly and efficiently with minimal disruption.
                            </p>
                            <div className="flex flex-wrap gap-4">
                                <Link to="/contact" className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-cyan-500 to-blue-500 text-white rounded-xl font-semibold hover:shadow-lg hover:shadow-cyan-500/30 transition-all duration-300">
                                    Start Deployment
                                    <FaArrowRight size={14} />
                                </Link>
                                <a href="tel:+12014099845" className="inline-flex items-center gap-2 px-8 py-4 border-2 border-white/30 text-white rounded-xl font-semibold hover:bg-white/10 transition-all duration-300">
                                    <FaPhone size={14} />
                                    Call Us Now
                                </a>
                            </div>
                        </div>
                        <div className="relative">
                            <div className="bg-gradient-to-br from-cyan-600/20 to-blue-600/20 backdrop-blur-sm rounded-2xl p-8 border border-white/10">
                                <img
                                    src="https://images.unsplash.com/photo-1531973576160-7125cd663d86?w=600&h=400&fit=crop"
                                    alt="IT Deployment"
                                    className="w-full h-80 object-cover rounded-xl shadow-2xl"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Services Section */}
            <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
                <div className="max-w-7xl mx-auto">
                    <div className="text-center mb-16">
                        <span className="inline-block px-4 py-2 bg-cyan-100 text-cyan-700 rounded-full text-sm font-semibold mb-4">Our Services</span>
                        <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Complete Deployment Solutions</h2>
                        <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                            Everything you need for successful IT deployments, from planning to handover
                        </p>
                    </div>

                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {services.map((service, index) => (
                            <div
                                key={index}
                                className="group bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100 hover:border-cyan-200"
                            >
                                <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${service.gradient} flex items-center justify-center text-white mb-6 group-hover:scale-110 transition-transform duration-300`}>
                                    {service.icon}
                                </div>
                                <h3 className="text-xl font-bold text-gray-900 mb-3">{service.title}</h3>
                                <p className="text-gray-600 leading-relaxed">{service.description}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Process Section */}
            <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-gray-50 to-cyan-50">
                <div className="max-w-7xl mx-auto">
                    <div className="text-center mb-16">
                        <span className="inline-block px-4 py-2 bg-cyan-100 text-cyan-700 rounded-full text-sm font-semibold mb-4">Our Process</span>
                        <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Streamlined Deployment Process</h2>
                    </div>

                    <div className="flex flex-col md:flex-row justify-between items-center gap-4">
                        {process.map((item, index) => (
                            <div key={index} className="flex-1 text-center relative">
                                <div className="w-16 h-16 mx-auto mb-4 bg-gradient-to-br from-cyan-500 to-blue-500 rounded-full flex items-center justify-center text-white text-2xl font-bold shadow-lg">
                                    {item.step}
                                </div>
                                <h3 className="text-lg font-bold text-gray-900 mb-1">{item.title}</h3>
                                <p className="text-sm text-gray-600">{item.description}</p>
                                {index < process.length - 1 && (
                                    <div className="hidden md:block absolute top-8 left-[60%] w-[80%] h-0.5 bg-gradient-to-r from-cyan-300 to-transparent"></div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-cyan-500 to-blue-500">
                <div className="max-w-4xl mx-auto text-center">
                    <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">Ready to Deploy?</h2>
                    <p className="text-xl text-cyan-100 mb-8">
                        Let us handle your IT deployment while you focus on your business.
                    </p>
                    <div className="flex flex-wrap justify-center gap-4">
                        <Link to="/contact" className="inline-flex items-center gap-2 px-8 py-4 bg-white text-cyan-600 rounded-xl font-semibold hover:shadow-lg transition-all duration-300">
                            Request Quote
                            <FaArrowRight size={14} />
                        </Link>
                        <a href="tel:+12014099845" className="inline-flex items-center gap-2 px-8 py-4 border-2 border-white text-white rounded-xl font-semibold hover:bg-white/10 transition-all duration-300">
                            <FaPhone size={14} />
                            Call Now
                        </a>
                    </div>
                </div>
            </section>

            <Footer />
        </div>
    );
};

export default ITDeploymentPage;
